import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'
import { BuilderCampaignType, CampaignCreateStep, CampaignCreateWorkflow, CampaignInfo, CreativeBuilderStep, CreativeBuilderType, CreativeCreateStep, CreativeType, DeliverStep, TargetStep } from 'workflows/CampaignCreateWorkflow'

import { Account, FrequencyUnit } from 'api/interfaces'
import { LocationTargetingType, TargetingType } from 'api/interfaces/CampaignTargeting'
import { CreateBulkDisplayAds } from 'api/interfaces/CreateAd'
import { DefaultDayParts } from 'components/targeters/DayPartPicker'
import { AdPlacementType } from 'features/builders/internet/retail/builderInternetRetailSlice'

import { InternetChannelDraft, QuickCampaignShared, TvChannelDraft, resolveCreativeLink, validImages } from './quickCampaignModels'

export const getFileType = (fileName: string) => {
  const parts = fileName.split('.')
  if (parts.length === 1 || (parts[0] === '' && parts.length === 2)) {
    return ''
  }
  return parts.pop()
}

const fileNameWithoutExtension = (fileName: string) => fileName.split('.')[0]

const campaignInfoOf = (shared: QuickCampaignShared, campaignName: string, budget: number): CampaignInfo => ({
  name: campaignName,
  budget: budget,
  contactName: '',
  email: '',
  phone: '',
  startDate: moment(shared.start),
  endDate: moment(shared.end),
  isTotalBudget: true
})

const smartTargetingOf = () => ({
  streetAddress: '',
  city: '',
  state: '',
  zip: '',
  walkInTargeting: false,
  walkInUsePrimary: false,
  walkInAddresses: [],
  competitorTargeting: false,
  competitors: []
})

const audienceTargetingOf = (audienceUrl: string) => ({
  demographics: {
    ageRanges: [],
    genders: [],
    householdIncomes: []
  },
  segments: [],
  uploadedAudienceUrl: audienceUrl
})

export const buildInternetCampaignWorkflow = (shared: QuickCampaignShared, campaignName: string, internet: InternetChannelDraft): CampaignCreateWorkflow => {
  const firstImage = validImages(internet)[0]
  return {
    id: uuidv4(),
    campaignCreateStep: CampaignCreateStep.CreativeCreate,
    campaignType: BuilderCampaignType.Display,
    createWorkflow: {
      creativeCreateStep: CreativeCreateStep.CampaignTypeSelect,
      creativeBuilderWorkflow: {
        creativeBuilderType: CreativeBuilderType.PreMade,
        creativeBuilderStep: CreativeBuilderStep.BusinessInfo
      }
    },
    targetWorkflow: {
      targetStep: TargetStep.Choose
    },
    deliverWorkflow: {
      deliverStep: DeliverStep.Delivery
    },
    campaignInfo: campaignInfoOf(shared, campaignName, internet.budget),
    creativeInfo: {
      creativeId: '',
      creativeType: CreativeType.Display,
      displayDetails: [
        {
          fileName: fileNameWithoutExtension(firstImage.fileName),
          displayImageUrl: firstImage.url,
          displayHeight: firstImage.height,
          displayWidth: firstImage.width,
          clickUrl: resolveCreativeLink(internet.links, firstImage.url)
        }
      ]
    },
    businessInfo: {},
    campaignTargeting: {
      smartTargeting: smartTargetingOf(),
      locationTargeting: {
        targetingType: LocationTargetingType.Region,
        regions: [],
        dmas: [],
        cities: [],
        zips: []
      },
      campaignDelivery: {
        dayParts: DefaultDayParts,
        frequency: internet.impressionFrequency,
        frequencyUnit: FrequencyUnit.week,
        inventorySettings: internet.inventorySettings,
        deviceSettings: internet.deviceSettings,
        targetCPM: internet.targetCPM,
        placement: AdPlacementType.Everywhere,
        categories: [],
        specific: []
      },
      targetingType: TargetingType.AudienceUpload,
      audienceTargeting: audienceTargetingOf(shared.audienceUrl)
    }
  }
}

export const buildTvCampaignWorkflow = (shared: QuickCampaignShared, campaignName: string, tv: TvChannelDraft): CampaignCreateWorkflow => ({
  id: uuidv4(),
  campaignCreateStep: CampaignCreateStep.CreativeCreate,
  campaignType: BuilderCampaignType.TV,
  createWorkflow: {
    creativeCreateStep: CreativeCreateStep.CampaignTypeSelect,
    creativeBuilderWorkflow: {
      creativeBuilderType: CreativeBuilderType.PreMade,
      creativeBuilderStep: CreativeBuilderStep.BusinessInfo
    }
  },
  targetWorkflow: {
    targetStep: TargetStep.Choose
  },
  deliverWorkflow: {
    deliverStep: DeliverStep.Delivery
  },
  campaignInfo: campaignInfoOf(shared, campaignName, tv.budget),
  creativeInfo: {
    creativeId: '',
    creativeType: CreativeType.Video,
    videoDetails: tv.uploadedVideos.map(video => ({
      baseVideoId: '',
      creativeId: '',
      kind: '',
      name: fileNameWithoutExtension(video.fileName),
      previewUrl: '',
      quality: '',
      script: '',
      sections: [],
      thumbnailUrl: '',
      type: '',
      uploadedUrl: video.url,
      clickUrl: resolveCreativeLink(tv.links, video.url)
    }))
  },
  businessInfo: {},
  campaignTargeting: {
    smartTargeting: smartTargetingOf(),
    locationTargeting: {
      targetingType: LocationTargetingType.USA,
      regions: [],
      dmas: [],
      cities: [],
      zips: [],
      uploadedAudienceUrl: shared.audienceUrl
    },
    campaignDelivery: {
      dayParts: DefaultDayParts,
      frequency: 0,
      frequencyUnit: FrequencyUnit.hour,
      inventorySettings: { targetApp: false, targetWeb: true },
      deviceSettings: { targetPc: true, targetPhone: true, targetTablet: true },
      placement: AdPlacementType.Everywhere,
      categories: [],
      specific: []
    },
    targetingType: TargetingType.AudienceUpload,
    audienceTargeting: audienceTargetingOf(shared.audienceUrl)
  }
})

export const buildRemainingDisplayAds = (internet: InternetChannelDraft, account: Pick<Account, 'id' | 'name'>, campaignId: string): CreateBulkDisplayAds => ({
  accountId: account.id,
  campaignId: campaignId,
  displayAds: validImages(internet)
    .slice(1)
    .map(image => ({
      name: fileNameWithoutExtension(image.fileName),
      destinationUrl: resolveCreativeLink(internet.links, image.url),
      companyName: account.name,
      uploadedUrl: image.url,
      htmlTag: '',
      width: image.width,
      height: image.height,
      mimeType: `image/${getFileType(image.fileName)}`,
      campaignIds: [campaignId]
    }))
})
